import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Badge } from "../components/ui/badge";
import { SectionHeader } from "../components/common/SectionHeader";
import { CTASection } from "../components/common/CTASection";
import { LogoCarousel } from "../components/common/LogoCarousel";
import { GeometricBackground } from "../components/decorations/GeometricBackground";
import { partnershipsData } from "../data/partnerships";

const PartnershipsPage = () => {
  const [activeFilter, setActiveFilter] = useState("all");

  // Build a safe public URL (works on / subpaths in Vite and CRA)
  const withBase = (path) => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path) || path.startsWith("data:")) return path;

    const viteBase =
      typeof import.meta !== "undefined" && import.meta.env?.BASE_URL
        ? import.meta.env.BASE_URL
        : null;

    const craBase =
      typeof process !== "undefined" && process.env?.PUBLIC_URL
        ? process.env.PUBLIC_URL
        : "";

    const base = (viteBase || craBase || "/").replace(/\/?$/, "/");
    return base + path.replace(/^\//, "");
  };

  const FILTERS = useMemo(
    () => [
      {
        key: "all",
        label: "All",
        predicate: () => true,
      },
      {
        key: "audioVisual",
        label: "Audio Visual",
        // Conference is included here now ✅
        predicate: (p) =>
          ["Audio", "AV", "Video", "Projectors", "Screens", "IPTV", "Conference"].includes(
            p.category
          ),
      },
      {
        key: "networking",
        label: "Networking",
        predicate: (p) =>
          ["Networking", "Cabling", "Connectors", "Testing", "IoT"].includes(p.category),
      },
      {
        key: "security",
        label: "Security",
        predicate: (p) => p.category === "Security",
      },
    ],
    []
  );

  const currentFilter = FILTERS.find((f) => f.key === activeFilter) || FILTERS[0];

  const filteredPartners = useMemo(() => {
    return partnershipsData.partners
      .filter(currentFilter.predicate)
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [currentFilter]);

  const counts = useMemo(() => {
    const obj = {};
    for (const f of FILTERS) {
      obj[f.key] = partnershipsData.partners.filter(f.predicate).length;
    }
    return obj;
  }, [FILTERS]);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-hero-pattern overflow-hidden">
        <GeometricBackground />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <Badge className="mb-4 bg-primary-foreground/10 text-primary-foreground border-primary-foreground/20">
              Strategic Partnerships
            </Badge>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              Our Technology Partners
            </h1>

            <p className="text-lg text-primary-foreground/80">{partnershipsData.intro}</p>
          </motion.div>
        </div>
      </section>

      {/* Partners Overview */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <SectionHeader
            subtitle="Trusted Brands"
            title="World-Class Technology Partners"
            description={partnershipsData.description}
            className="mb-16"
          />
          <LogoCarousel logos={partnershipsData.partners} />
        </div>
      </section>

      {/* Filtered Partners Grid */}
      <section className="section-padding bg-card">
        <div className="container-custom">
          <SectionHeader
            subtitle="Browse by Category"
            title="Solutions Across Every Domain"
            className="mb-10"
          />

          {/* Filters */}
          <div className="flex flex-wrap gap-2 items-center justify-center mb-10">
            {FILTERS.map((f) => {
              const isActive = f.key === activeFilter;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setActiveFilter(f.key)}
                  aria-pressed={isActive}
                  className={[
                    "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition",
                    isActive
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background text-foreground border-border hover:bg-muted/50",
                  ].join(" ")}
                >
                  <span className="font-medium">{f.label}</span>
                  <span
                    className={[
                      "text-xs rounded-full px-2 py-0.5",
                      isActive
                        ? "bg-primary-foreground/20 text-primary-foreground"
                        : "bg-muted text-muted-foreground",
                    ].join(" ")}
                  >
                    {counts[f.key] ?? 0}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Grid */}
          <motion.div
            key={activeFilter}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4"
          >
            {filteredPartners.map((partner) => (
              <div
                key={partner.name}
                className="group rounded-2xl border border-border bg-background p-4 hover:shadow-md transition"
                title={partner.name}
              >
                <div className="h-14 w-full rounded-xl bg-white flex items-center justify-center overflow-hidden relative">
                   <img
    src={withBase(partner.logo)}
    alt={`${partner.name} logo`}
    className="h-10 w-auto object-contain"
    loading="lazy"
    onError={(e) => {
      // If a logo is missing, the name below still shows,
      // so just hide the broken image icon.
      e.currentTarget.style.display = "none";
    }}
  />
                </div>

                <p className="mt-3 text-xs font-medium text-foreground text-center line-clamp-2">
                  {partner.name}
                </p>

                <p className="mt-1 text-[11px] text-muted-foreground text-center">
                  {partner.category}
                </p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        title="Partner With Us"
        description="Interested in becoming a Voitech Solutions partner? Let's explore collaboration opportunities."
        primaryButtonText="Contact Us"
        primaryButtonLink="/contact"
      />
    </div>
  );
};

export default PartnershipsPage;
